import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Button from 'Mui/Button';
import MuiDialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import { system900 } from 'Mui/styles/color';
import { dp08 } from 'Mui/styles/shadow';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/core';

const useStyles = makeStyles({
    dialog: {
        width: '100%',
        maxWidth: '320px',
        borderRadius: '16px',
        boxShadow: dp08.boxShadow
    },
    title: {
        padding: '12px 24px',
        whiteSpace: 'pre-wrap'
    },
    content: {
        '&[class^="MuiDialogContent-root"]': {
            padding: '0'
        }
    },
    closeIcon: {
        width: '24px',
        height: '24px',
        position: 'absolute',
        top: '15px',
        right: '15px',
        cursor: 'pointer',
        color: system900.color,
        'z-index': 1
    },
    footer: {
        display: 'flex',
        justifyContent: 'center',
        padding: '16px',
        flexDirection: 'column'
    },
    button: {
        borderRadius: '20px',
        minWidth: '125px',
        '&.MuiButtonBase-root': {
            margin: '0'
        },
        '&:not(:first-child)': {
            marginTop: '8px'
        }
    },
    largeButton: {
        minWidth: '182px',
        borderRadius: '24px',
        padding: '12px 8px'
    }
});

function PopupWithWaitingButton({
    isOpen = true,
    showClose,
    maxWidth = 'xs',
    onClose,
    onConfirm,
    children,
    title,
    titleAlign = 'center',
    disableBackdropClick,
    button,
    buttonVariant = 'contained',
    buttonColor = 'primary',
    buttonStyle,
    buttonDisable,
    buttonSize = 'medium',
    className,
    backdropStyle = {},
    waitingTime = 3
}) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [time, setTime] = useState(waitingTime || 0);

    useEffect(() => {
        let timer = null;

        if (isOpen && time > 0) {
            timer = setTimeout(() => {
                setTime(c => (c -= 1));
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [isOpen, time]);

    useEffect(() => {
        if (isOpen === true) setTime(waitingTime);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    useEffect(() => {
        setTime(waitingTime);
    }, [waitingTime]);

    return (
        <MuiDialog
            disableAutoFocus
            disableEnforceFocus
            disableRestoreFocus
            disableBackdropClick={disableBackdropClick}
            onClose={onClose}
            open={isOpen}
            maxWidth={maxWidth}
            classes={{
                paper: `${classes.dialog} ${className}`
            }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(255, 255, 255, 0.56)',
                    backdropFilter: 'blur(4px)',
                    ...backdropStyle
                }
            }}
        >
            {showClose ? (
                <IconButton onClick={onClose} size="small" className={classes.closeIcon}>
                    <CloseIcon />
                </IconButton>
            ) : null}
            {Boolean(title) && (
                <DialogTitle disableTypography className={classes.title}>
                    <Typography align={titleAlign} variant="h6">
                        {title}
                    </Typography>
                </DialogTitle>
            )}
            <DialogContent classes={{ root: classes.content }}>{children}</DialogContent>
            {
                <>
                    {button && (
                        <DialogActions
                            className={classes.footer}
                            css={css(ActionContainerStyle(Boolean(time)))}
                        >
                            <Button
                                variant={buttonVariant}
                                color={buttonColor}
                                onClick={onConfirm}
                                disabled={buttonDisable || time > 0}
                                className={clsx(
                                    classes.button,
                                    buttonStyle,
                                    buttonSize === 'large' && classes.largeButton
                                )}
                            >
                                {button}
                            </Button>
                        </DialogActions>
                    )}
                    {Boolean(time) && (
                        <Typography css={css(WaitingMessageStyle)}>
                            {t('label.watting_seconds_to_action', { seconds: time })}
                        </Typography>
                    )}
                </>
            }
        </MuiDialog>
    );
}

PopupWithWaitingButton.propTypes = {
    isOpen: PropTypes.bool,
    showClose: PropTypes.bool,
    maxWidth: PropTypes.string,
    onCancel: PropTypes.func,
    onClose: PropTypes.func,
    onConfirm: PropTypes.func,
    children: PropTypes.node,
    title: PropTypes.string,
    titleAlign: PropTypes.string,
    disableBackdropClick: PropTypes.bool,
    startButton: PropTypes.string,
    startButtonVariant: PropTypes.string,
    startButtonColor: PropTypes.string,
    startButtonStyle: PropTypes.string,
    startButtonDisable: PropTypes.bool,
    endButton: PropTypes.string,
    endButtonVariant: PropTypes.string,
    endButtonColor: PropTypes.string,
    endButtonStyle: PropTypes.string,
    endButtonDisable: PropTypes.bool,
    className: PropTypes.string,
    backdropStyle: PropTypes.object
};

export default PopupWithWaitingButton;

const WaitingMessageStyle = css`
    text-align: center;
    padding-bottom: 16px;
    font-weight: 500;
    font-size: 10px;
    line-height: 14px;
    text-align: center;
    letter-spacing: 1.5px;
`;

const ActionContainerStyle = isWaiting => css`
    padding: ${isWaiting ? '16px 16px 4px' : '16px'};
`;
